import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Check, Loader2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 100 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    reload();
  };

  const click = async (n) => {
    if (!n.is_read) await api.post(`/notifications/${n.id}/read`);
    if (n.link_url) nav(n.link_url); else reload();
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="notifications-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="axia-overline">Inbox</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Notifications</h1>
          <p className="text-sm text-slate-500 mt-2">{unread} unread of {items.length}</p>
        </div>
        <Button variant="outline" onClick={markAll} disabled={unread === 0} data-testid="page-mark-all-read">
          <Check className="w-3.5 h-3.5 mr-2" /> Mark all read
        </Button>
      </div>

      {loading ? <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        : items.length === 0 ? (
          <div className="axia-card p-12 text-center text-slate-500" data-testid="empty-notifications">
            <Bell className="w-7 h-7 mx-auto text-slate-300" />
            <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">No notifications yet</h2>
            <p className="text-sm mt-1">When teammates send kudos, request feedback, or your reviews need attention, they'll show up here.</p>
          </div>
        ) : (
          <ul className="axia-card divide-y divide-slate-100">
            {items.map((n) => (
              <li key={n.id}>
                <button onClick={() => click(n)} className={`w-full text-left px-5 py-4 hover:bg-slate-50 transition flex items-start gap-3 ${!n.is_read ? "bg-blue-50/30" : ""}`} data-testid={`notif-row-${n.id}`}>
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-slate-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base font-semibold text-slate-900 leading-snug">{n.title}</div>
                    {n.body && <div className="text-sm text-slate-600 mt-1">{n.body}</div>}
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-2">
                      {n.created_at ? formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }) : ""} · {(n.type || "").replace(/_/g, " ")}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
